<template>
  <v-main>

    <v-container
        id="invoice-container"
        fluid
        tag="section"
    >

      <base-material-card
          icon="mdi-text-box-plus-outline"
          class="px-5 py-3"
          ref="form"
      >

        <shared-page-messages :error-messages="errorMessages"
                              :warning-messages="warningMessages"
                              :success-messages="successMessages">
        </shared-page-messages>

        <v-form ref="invoiceForm"
                v-model="valid"
                @submit.prevent="validate"
                lazy-validation
        >
          <v-row>
            <v-col
                cols="12"
                sm="6"
            >
              <v-select
                  v-model="invoiceType"
                  label="Invoice Type"
                  :items="computedInvoiceTypeItems"
                  item-value="id"
                  item-text="name"
                  :rules="rules.required"
              ></v-select>
            </v-col>
          </v-row>

          <v-row v-if="computedSelectClaim">
            <v-col
                cols="12"
                sm="6"
            >
              <v-select
                  v-model="computedClaim"
                  label="Claim"
                  :items="computedClaimList"
                  item-text="names"
                  item-value="id"
                  return-object
                  :rules="rules.required"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col
                cols="12"
                sm="6"
            >
              <v-checkbox v-model="invoiceAllUsers">
                <template v-slot:label>
                  <div class="invoice-all-members-checkbox-label">
                    Invoice All Active Members
                  </div>
                </template>
              </v-checkbox>

              <v-combobox
                  v-if="!invoiceAllUsers"
                  v-model="invoice.users"
                  label="Member"
                  placeholder="Start typing to search"
                  :items="computedUserList"
                  :loading="isLoadingUsers"
                  :search-input.sync="userSearchString"
                  @input.native="nativeUserSearchString=$event.srcElement.value"
                  item-text="names"
                  item-value="id"
                  return-object
                  persistent-placeholder
                  no-filter
                  cache-items
                  validate-on-blur
                  multiple
                  deletable-chips
                  chips
                  :rules="rules.validateMembers"
              ></v-combobox>

            </v-col>
          </v-row>

          <v-row>
            <v-col
                cols="6"
                sm="6"
            >
              <v-text-field
                  v-model="invoice.amount"
                  label="Amount(s)"
                  :rules="rules.currency"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col
                cols="12"
                sm="9"
            >
              <v-text-field
                  v-model="invoice.description"
                  label="Description"
                  type="text"
                  :rules="rules.required"
              ></v-text-field>
            </v-col>

          </v-row>

          <v-row>
            <v-col
                cols="12"
                sm="3"
            >
              <v-text-field
                  v-model="computedInvoiceDate"
                  label="Invoice Date"
                  type="date"
                  :rules="rules.date"
              ></v-text-field>
            </v-col>

          </v-row>

          <v-row>
            <v-col
                cols="12"
                sm="3"
            >
              <v-text-field
                  v-model="computedDueDate"
                  label="Due Date"
                  type="date"
                  :rules="rules.date"
              ></v-text-field>
            </v-col>

          </v-row>


          <v-row>
            <v-col
                cols="6"
                sm="4"
            >
              <div class="my-2">
                <v-btn
                    :disabled="processing"
                    :loading="processing"
                    color="primary"
                    dark
                    type="submit"
                >
                  Submit
                </v-btn>
              </div>
            </v-col>
          </v-row>

        </v-form>


      </base-material-card>

      <div class="text-center">
        <v-dialog
          v-model="processing"
          hide-overlay
          persistent
          width="400"
        >
          <v-card class="text-center">
            <v-card-text class="text-center">
              <v-row>
                <v-col cols="12">
                  <p class="text-center">Generating invoice(s), please stand by</p>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-progress-circular
                      :size="70"
                      :width="10"
                      indeterminate
                      color="primary"
                  ></v-progress-circular>
                </v-col>
              </v-row>
            </v-card-text>

          </v-card>

        </v-dialog>
        </div>


    </v-container>

  </v-main>
</template>

<script>
import api from '@/services/api'
import validation from "@/utils/validation"
import moment from "moment";
import constants from "@/utils/constants";
import SharedPageMessages from '../shared/PageMessages.vue'

export default {
  name: 'CoreInvoiceView',
  components: {SharedPageMessages},

  created() {
  },

  computed: {
    computedClaim: {
      get() {
        if (this.invoice.claim === undefined || this.invoice.claim === null) {
          return
        }
        return this.claims.find(claim => this.invoice.claim.id === claim.id)
      },
      set(value) {
        this.invoice.claim = value
      }
    },
    computedClaimList: {
      get() {
        return this.claims
            .filter(claim => claim.status !== 'REJECTED')
            .map(claim => ({id: claim.id, names: `${claim.user.names} (${claim.dependent.names})`}))
      }
    },
    computedUserList: {
      get() {
        return this.users
            .map(u => ({ id: u.id, names: u.names}))
      }
    },
    computedInvoiceDate: {
      get() {
        if (this.invoice.invoiceDate == null) {
          return
        }
        return moment(this.invoice.invoiceDate, 'YYYY-MM-DD').format('YYYY-MM-DD')
      },
      set(value) {
        this.invoice.invoiceDate = value
      }
    },
    computedDueDate: {
      get() {
        if (this.invoice.dueDate == null) {
          return
        }
        return moment(this.invoice.dueDate, 'YYYY-MM-DD').format('YYYY-MM-DD')
      },
      set(value) {
        this.invoice.dueDate = value
      }
    },
    computedSelectClaim: {
      get() {
        return (this.invoiceType === 'REPLENISHMENT')
      }
    },
    computedInvoiceTypeItems: {
      get: function () {
        return Object.keys(this.invoiceTypeMap)
            .map((key) => ({id: key, name: this.invoiceTypeMap[key]}))
      }
    }
  },

  watch: {
    errorMessages: function () {
      scroll(0, 0)
    },
    successMessages: function () {
      scroll(0, 0)
    },
    warningMessages: function () {
      scroll(0, 0)
    },
    userSearchString(val) {
      if (!this.isLoadingUsers && (val.length > 2)) {
        this.fetchUsers(val)
      }
    },
    invoiceType: function (val) {
      this.invoice.description = this.invoiceTypeMap[val]
      if (val === 'REPLENISHMENT') {
        this.fetchClaims()
      }
    },
    computedClaim: function(val) {
      this.invoice.description = ''
      const claim = this.claims.find(item => (val.id === item.id))
      if (claim !== undefined) {
        this.invoice.description = `Replenishment for ${claim.user.names} for ${claim.dependent.names}`
      }
    }
  },

  methods: {
    clearErrors() {
      this.errorMessages = []
      this.warningMessages = []
      this.successMessages = []
    },
    validate() {
      if(this.$refs.invoiceForm.validate()) {
        this.createInvoices()
      }
    },
    fetchClaims() {
      api.fetchClaims()
          .then(response => {
            this.claims = response.data
          })
          .catch(err => {
            this.handleError(err)
          })
    },
    fetchUsers(val) {
      this.clearErrors()
      this.isLoadingUsers = true
      let nameParts = val.split(' ')
      let firstPart = nameParts.shift()
      let lastPart = nameParts.pop() || firstPart
      let filters = {
        firstName: firstPart,
        lastName: lastPart,
        searchCondition: 'OR'
      }
      api.fetchUsers(filters)
          .then(response => {
            this.users = this.users.concat(response.data.content)
          })
          .catch(err => {
            this.handleError(err)
          })
          .finally(this.isLoadingUsers = false)
    },
    createInvoices() {
      this.processing = true
      const payload = {
        description: this.invoice.description,
        amount: this.invoice.amount,
        dueDate: this.invoice.dueDate,
        invoiceDate: this.invoice.invoiceDate,
        invoiceAllUsers: this.invoiceAllUsers
      }
      if (this.invoice.claim !== null && this.invoice.claim.id !== undefined) {
        payload.claimId = this.invoice.claim.id
      }

      if (!this.invoiceAllUsers && this.invoice.users.length > 0) {
        payload.userIds = this.invoice.users.map(user => user.id)
      }

      api.createInvoices(payload)
          .then(response => {
            this.processing = false
            if (response.status === 200) {
              if (payload.claimId !== undefined) {
                const claim = this.claims.find(item => item.id === payload.claimId)
                this.$router.push(`/member/${claim.user.id}/claim/${claim.id}/view`)
              } else {
                this.$router.push(`/invoices/view`)
              }
            }
          })
          .catch(err => {
            this.handleError(err)
          })
    },
    handleError(err) {
      let errorMessage = api.parseResponseError(err)
      this.errorMessages.push(errorMessage)
    }
  },

  data() {
    return {
      valid: true,
      errorMessages: [],
      successMessages: [],
      warningMessages: [],
      invoice: {
        claim: null,
        users: null,
        amount: null,
        description: null,
        invoiceDate: null,
        dueDate: null
      },
      claim: {
        id: null,
        user: {}
      },
      claims: [],
      users: [],
      selectedUsers: [],
      userSearchString: '',
      nativeUserSearchString: '',
      invoiceType: null,
      selectClaim: false,
      invoiceTypeMap: constants.getInvoiceTypeMap(),
      paymentStatusMap: constants.getPaymentStatusMap(),
      invoiceTypeItems: [
          'Replenishment',
          'Registration',
          'Annual Fee'
      ],
      processing: false,
      isLoadingUsers: false,
      invoiceAllUsers: false,
      rules: {
        required: [
          v => validation.isRequired(v) || 'required'
        ],
        currency: [
          v => validation.isCurrency(v) || 'must be in currency format'
        ],
        date: [
            v => validation.isDate(v) || 'must be a valid date in the format mm/dd/yyyy'
        ],
        validateMembers: [
          v => validation.isRequired(v) || 'required',
          v => validation.isSublistOfObjectsList(v, this.computedUserList) || 'must be in the list of members'
        ]
      }
    }
  }
}
</script>


<style lang="sass" scoped>
.invoice-all-members-checkbox-label
  color: #333
</style>
